// Shared navigation and section-link behaviour for every HIT page.
(() => {
  const header = document.getElementById('hit-site-header');
  if (!header) return;
  const mobile = header.querySelector('.mobile-nav');
  const dropdowns = [...header.querySelectorAll('.desktop-dropdown')];
  const menus = [mobile, ...dropdowns].filter(Boolean);
  const mobileLayout = matchMedia('(max-width: 1200px)');
  const mobilePanel = mobile?.querySelector('nav');
  const scrollHint = document.createElement('p');
  scrollHint.className = 'menu-scroll-hint';
  scrollHint.hidden = true;
  scrollHint.setAttribute('aria-hidden', 'true');
  mobile?.append(scrollHint);
  function updateScrollHint() {
    if (!mobilePanel || !mobile.open) return;
    const hasOverflow = mobilePanel.scrollHeight > mobilePanel.clientHeight + 2;
    const atBottom = mobilePanel.scrollHeight - mobilePanel.clientHeight - mobilePanel.scrollTop < 4;
    scrollHint.hidden = !hasOverflow || atBottom;
    scrollHint.textContent = 'Scroll for more ↓';
    scrollHint.style.top = `${mobilePanel.offsetTop + mobilePanel.offsetHeight - 28}px`;
    scrollHint.style.left = `${mobilePanel.offsetLeft}px`;
    scrollHint.style.width = `${mobilePanel.offsetWidth}px`;
  }
  mobilePanel?.addEventListener('scroll', updateScrollHint, { passive: true });
  function clearance() {
    const bottom = header.getBoundingClientRect().bottom;
    header.style.setProperty('--menu-available-height', `${Math.max(44, (window.visualViewport ? visualViewport.height + visualViewport.offsetTop : innerHeight) - bottom - 12)}px`);
    document.documentElement.style.setProperty('--header-clearance', `${header.offsetHeight}px`);
    updateScrollHint();
  }
  if ('ResizeObserver' in window) new ResizeObserver(clearance).observe(header);
  window.addEventListener('resize', clearance, { passive: true });
  window.visualViewport?.addEventListener('resize', clearance, { passive: true });
  window.addEventListener('scroll', clearance, { passive: true });
  mobile?.addEventListener('toggle', () => {
    mobile.querySelector('summary').setAttribute('aria-label', mobile.open ? 'Close navigation menu' : 'Open navigation menu');
    clearance();
  });
  dropdowns.forEach(menu => menu.addEventListener('toggle', () => {
    if (menu.open) dropdowns.forEach(other => { if (other !== menu) other.open = false; });
  }));
  // Keyboard users should not leave an open menu covering the page behind them.
  menus.forEach(menu => menu.addEventListener('keydown', event => {
    if (event.key !== 'Tab') return;
    requestAnimationFrame(() => {
      if (menu.open && !menu.contains(document.activeElement)) menu.open = false;
    });
  }));
  document.addEventListener('click', event => {
    menus.forEach(menu => {
      if (!menu.contains(event.target)) menu.open = false;
      // Let the browser activate links before hiding them (including Safari).
      else if (event.target.closest('a')) requestAnimationFrame(() => { menu.open = false; });
    });
  });
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') menus.forEach(menu => {
      if (menu.open) { menu.open = false; menu.querySelector('summary').focus(); }
    });
  });
  mobileLayout.addEventListener('change', () => {
    const hiddenMenus = mobileLayout.matches ? dropdowns : [mobile].filter(Boolean);
    const focusWasHidden = hiddenMenus.some(menu => menu.contains(document.activeElement));
    menus.forEach(menu => { menu.open = false; });
    if (focusWasHidden) (mobileLayout.matches ? mobile?.querySelector('summary') : header.querySelector('.brand'))?.focus();
    clearance();
  });

  // Shopify draft views can share a path, so compare the view as well as the path.
  const current = new URL(location.href);
  document.querySelectorAll('#hit-site-header nav a, .hit-footer nav a').forEach(link => {
    const target = new URL(link.href, current);
    if (!target.hash && target.origin === current.origin && target.pathname === current.pathname &&
        target.searchParams.get('view') === current.searchParams.get('view')) link.setAttribute('aria-current', 'page');
  });

  function revealSection(hash) {
    let id;
    try { id = decodeURIComponent(hash.slice(1)); } catch { return; }
    const destination = document.getElementById(id);
    if (!destination) return;
    // A linked answer must be visible, including any enclosing disclosures.
    let container = destination.closest('details');
    while (container) {
      container.open = true;
      container = container.parentElement?.closest('details');
    }
    return destination;
  }
  function focusSection(destination) {
    const temporaryFocus = !destination.hasAttribute('tabindex');
    if (temporaryFocus) destination.setAttribute('tabindex', '-1');
    destination.focus({ preventScroll: true });
    if (temporaryFocus) destination.addEventListener('blur', () => destination.removeAttribute('tabindex'), { once: true });
  }
  function clearSectionHash() {
    // Keep provider state and query parameters; only consume our own section anchor.
    history.replaceState(history.state, '', location.pathname + location.search);
  }
  function revealHash() {
    const destination = revealSection(location.hash);
    if (!destination) return;
    clearSectionHash();
    requestAnimationFrame(() => {
      destination.scrollIntoView({ block: 'start' });
      focusSection(destination);
    });
  }
  window.addEventListener('hashchange', revealHash);

  // A section shortcut scrolls within this page, rather than adding a Back step.
  // Full Shopify URLs can point to this same page; different draft views cannot.
  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a');
    if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    const target = new URL(link.href, location.href);
    if (!target.hash || target.origin !== location.origin || target.pathname !== location.pathname || target.search !== location.search) return;
    const destination = revealSection(target.hash);
    if (!destination) return;
    event.preventDefault();
    if (revealSection(location.hash)) clearSectionHash();
    requestAnimationFrame(() => {
      destination.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      focusSection(destination);
    });
  });
  clearance();
  if (location.hash) revealHash();
})();
